import { Alert, Button, Spin } from "@arco-design/web-react";
import { ExternalLink, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";

import { useCustomizationMobileWorkbenchSession } from "..";

/** 手机端行程计划页，通过共享会话加载受信 SSO iframe。 */
export function CustomizationMobileItineraryPage() {
  const {
    itinerarySsoByDemandId,
    resolveItinerarySso,
    runtime,
  } = useCustomizationMobileWorkbenchSession();
  const [frameReloadKey, setFrameReloadKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const viewModel = runtime.viewModel;
  const demandId = viewModel.displayDemandId ?? "";
  const itineraryTarget = runtime.sharedRuntime.itineraryEditorTarget;
  const hasItineraryTarget = Boolean(
    demandId &&
      (viewModel.currentVersion?.hasItinerary || itineraryTarget?.demandId === demandId),
  );
  const itineraryGenerating = Boolean(
    demandId &&
      !viewModel.currentVersion?.hasItinerary &&
      itineraryTarget?.type === "generate" &&
      itineraryTarget.demandId === demandId,
  );
  const resolution = demandId ? itinerarySsoByDemandId[demandId] : undefined;

  useEffect(() => {
    if (hasItineraryTarget && demandId && !resolution) {
      void resolveItinerarySso(demandId);
    }
  }, [demandId, hasItineraryTarget, resolution, resolveItinerarySso]);

  if (viewModel.loading) {
    return (
      <main className="customization-mobile-workbench-page customization-mobile-itinerary-page" aria-label="手机行程计划页">
        <div className="customization-mobile-loading" role="status">
          <Spin dot />
          <span>加载中...</span>
        </div>
      </main>
    );
  }

  return (
    <main className="customization-mobile-workbench-page customization-mobile-itinerary-page" aria-label="手机行程计划页">
      <span className="customization-mobile-visually-hidden">{viewModel.projectName}</span>

      <section className="customization-mobile-itinerary-toolbar" aria-label="手机行程工具栏">
        <div className="customization-mobile-itinerary-toolbar-copy">
          <strong>{viewModel.currentVersion?.versionNumber ?? "当前版本"} 行程计划</strong>
          <span>
            {viewModel.currentVersion?.hasItinerary
              ? "已固化行程"
              : itineraryGenerating
                ? "行程生成中"
                : "暂未生成行程"}
          </span>
        </div>
        <div>
          <Button
            aria-label="刷新行程状态"
            icon={<RefreshCw size={17} />}
            disabled={!demandId || refreshing}
            loading={refreshing}
            onClick={async () => {
              if (!demandId) {
                return;
              }
              setRefreshing(true);
              setFrameReloadKey((current) => current + 1);
              try {
                await Promise.all([
                  runtime.sharedRuntime.refreshWorkbench(demandId),
                  hasItineraryTarget
                    ? resolveItinerarySso(demandId, true)
                    : Promise.resolve(),
                ]);
              } finally {
                setRefreshing(false);
              }
            }}
          />
          {resolution?.status === "ready" ? (
            <a
              className="customization-mobile-itinerary-open"
              href={resolution.editorUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="在新窗口打开行程"
            >
              <ExternalLink size={17} />
            </a>
          ) : null}
        </div>
      </section>

      {viewModel.hasManualDraft ? (
        <Alert
          className="customization-mobile-alert customization-mobile-itinerary-version-banner"
          type="warning"
          content="当前草稿尚未进入此行程，页面展示的是已保存需求版本。"
          showIcon
        />
      ) : null}

      <section className="customization-mobile-itinerary-stage" aria-label="手机行程 iframe 区域">
        {!hasItineraryTarget ? (
          <div className="customization-mobile-itinerary-empty">
            <strong>当前版本暂无行程</strong>
            <span>请从需求定制页的更多操作生成行程。</span>
          </div>
        ) : resolution?.status === "error" ? (
          <div className="customization-mobile-itinerary-empty" role="alert">
            <strong>行程计划暂时无法打开</strong>
            <span>单点登录地址获取失败，请稍后重试。</span>
            <Button
              icon={<RefreshCw size={15} />}
              onClick={() => {
                void resolveItinerarySso(demandId, true);
              }}
            >
              重新打开行程
            </Button>
          </div>
        ) : resolution?.status === "ready" ? (
          <iframe
            key={`${demandId}:${frameReloadKey}:${resolution.editorUrl}`}
            className="customization-mobile-itinerary-frame"
            src={resolution.editorUrl}
            title="行程计划智能体"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="customization-mobile-itinerary-empty" role="status" aria-live="polite">
            <Spin dot />
            <strong>正在打开行程计划智能体</strong>
            <span>正在建立当前用户的单点登录。</span>
          </div>
        )}
      </section>
    </main>
  );
}
